import React, { useEffect, useState } from 'react';
import { api, formatApiErrorDetail } from '../lib/api';
import { Btn, Card, Input, Label, PageHeader } from '../components/Ui';
import { Plus, Trash2, Save } from 'lucide-react';

const EMPTY = { code: '', name: '', inbox_email: '', wasabi_prefix: '', active: true };

export default function Companies() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // company being edited (or 'new')
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');

  const load = async () => {
    const { data } = await api.get('/api/companies');
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing('new'); setForm(EMPTY); setError(''); };
  const openEdit = (c) => { setEditing(c.id); setForm({ ...c, inbox_email: c.inbox_email || '' }); setError(''); };
  const cancel = () => { setEditing(null); setForm(EMPTY); setError(''); };

  const save = async () => {
    setError('');
    try {
      const body = { ...form };
      if (!body.inbox_email) delete body.inbox_email;
      if (editing === 'new') {
        await api.post('/api/companies', body);
      } else {
        await api.patch(`/api/companies/${editing}`, body);
      }
      await load();
      cancel();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar/desactivar ${c.code}?`)) return;
    try {
      await api.delete(`/api/companies/${c.id}`);
      await load();
    } catch (e) {
      alert(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <>
      <PageHeader title="Empresas" subtitle={`${items.length} empresa${items.length === 1 ? '' : 's'}`} testid="companies-header">
        <Btn data-testid="new-company-btn" onClick={openNew}>
          <Plus size={14} /> Nueva
        </Btn>
      </PageHeader>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card testid="companies-list" className="lg:col-span-2">
          <table className="w-full text-xs">
            <thead className="bg-surface text-muted">
              <tr className="text-[10px] uppercase tracking-wide2">
                <th className="px-3 py-2 text-left font-bold">Código</th>
                <th className="px-3 py-2 text-left font-bold">Nombre</th>
                <th className="px-3 py-2 text-left font-bold">Email entrada</th>
                <th className="px-3 py-2 text-left font-bold">Prefijo Wasabi</th>
                <th className="px-3 py-2 text-left font-bold">Activa</th>
                <th className="px-3 py-2 text-right font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} data-testid={`company-row-${c.id}`} className="border-t border-border hover:bg-surface">
                  <td className="px-3 py-2 font-mono font-bold">{c.code}</td>
                  <td className="px-3 py-2">{c.name}</td>
                  <td className="px-3 py-2 font-mono">{c.inbox_email || '—'}</td>
                  <td className="px-3 py-2 font-mono">{c.wasabi_prefix}</td>
                  <td className="px-3 py-2">{c.active ? 'Sí' : 'No'}</td>
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <Btn data-testid={`edit-company-${c.id}`} size="sm" variant="secondary" onClick={() => openEdit(c)}>Editar</Btn>
                    <Btn data-testid={`delete-company-${c.id}`} size="sm" variant="ghost" onClick={() => remove(c)}>
                      <Trash2 size={14} />
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {editing !== null && (
          <Card testid="company-form" className="self-start">
            <div className="border-b border-ink/90 px-4 py-3">
              <h2 className="text-sm font-bold uppercase tracking-wide2">
                {editing === 'new' ? 'Nueva empresa' : `Editar #${editing}`}
              </h2>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <Label>Código *</Label>
                <Input data-testid="company-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
              </div>
              <div>
                <Label>Nombre *</Label>
                <Input data-testid="company-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>Email entrada</Label>
                <Input data-testid="company-email" type="email" value={form.inbox_email} onChange={(e) => setForm({ ...form, inbox_email: e.target.value })} />
              </div>
              <div>
                <Label>Prefijo Wasabi *</Label>
                <Input data-testid="company-prefix" value={form.wasabi_prefix} onChange={(e) => setForm({ ...form, wasabi_prefix: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-xs">
                <input data-testid="company-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Activa
              </label>
              {error && <div data-testid="company-error" className="border border-danger text-danger text-xs px-3 py-2">{error}</div>}
              <div className="flex gap-2">
                <Btn data-testid="company-save" onClick={save}><Save size={14} /> Guardar</Btn>
                <Btn data-testid="company-cancel" variant="secondary" onClick={cancel}>Cancelar</Btn>
              </div>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
